import { useEffect, useState, useContext, useRef } from 'react';
import { ServersAPI } from '../api';
import { gameInitials, gameColor, getGameIconPath, fetchRawgImageUrl } from '../utils/gameIcon';
import { UserContext } from '../context/UserContext';

export default function ServersPage() {
  const { user } = useContext(UserContext);
  // Listing/filter/pagination
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [game, setGame] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Admin create/edit
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', game: '', host: '', port: '', queryType: '', description: '', visibility: 'public', tags: '', password: '' });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', game: '', host: '', port: '', queryType: '', description: '', visibility: 'public', tags: '', password: '' });
  // Live status
  const [statuses, setStatuses] = useState({}); // id -> live info
  const [statusTick] = useState(0);
  const [visibleIds, setVisibleIds] = useState(new Set());
  const observerRef = useRef(null);
  const elementsRef = useRef(new Map()); // id -> element
  const [rawgImages, setRawgImages] = useState({}); // gameName -> url | null (from multi-source resolver)

  const isAdmin = (user?.role === 'admin') || (Array.isArray(user?.roles) && user.roles.includes('admin'));
  const canCreate = !!user && isAdmin;

  async function load() {
    try {
      setLoading(true);
      setError('');
      const res = await ServersAPI.list({ q, game, page, pageSize });
      const raw = res?.items || [];
      const filtered = !user ? raw.filter(it => String(it.visibility || 'public') === 'public') : raw;
      setItems(filtered);
      setTotal(res?.total != null ? ( !user ? filtered.length : res.total ) : filtered.length);
    } catch (e) {
      setError('Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize]);

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
    load();
  }

  // Status polling for visible cards
  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      if (!Array.isArray(items) || items.length === 0) return;
      const idsToFetch = items.map(it => it.id).filter(id => visibleIds.has(id));
      if (idsToFetch.length === 0) return;
      try {
        const results = await Promise.all(idsToFetch.map(async (id) => {
          try {
            const res = await ServersAPI.status(id);
            return { id, live: res?.live || null };
          } catch { return { id, live: null }; }
        }));
        if (cancelled) return;
        setStatuses(prev => {
          const next = { ...prev };
          for (const r of results) next[r.id] = r.live;
          return next;
        });
      } catch {}
    }
    fetchAll();
    const iv = setInterval(() => { if (!cancelled) fetchAll(); }, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [items, statusTick, visibleIds]);

  // IntersectionObserver to track visible cards
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      setVisibleIds(prev => {
        const next = new Set(Array.from(prev));
        for (const e of entries) {
          const id = Number(e.target.dataset.serverId);
          if (!id) continue;
          if (e.isIntersecting) next.add(id); else next.delete(id);
        }
        return next;
      });
    }, { root: null, rootMargin: '100px', threshold: 0.01 });
    observerRef.current = observer;
    // observe current elements
    for (const el of elementsRef.current.values()) observer.observe(el);
    return () => { observer.disconnect(); observerRef.current = null; };
  }, []);

  function bindCardRef(id) {
    return (el) => {
      if (!el) return;
      el.dataset.serverId = String(id);
      elementsRef.current.set(id, el);
      if (observerRef.current) observerRef.current.observe(el);
    };
  }

  async function checkStatus(id) {
    try {
      const res = await ServersAPI.status(id);
      const info = res?.live || {};
      const online = info.online === true;
      const lat = typeof info.latencyMs === 'number' ? `${info.latencyMs}ms` : '–';
      const players = (typeof info.players === 'number' && typeof info.maxPlayers === 'number') ? `${info.players}/${info.maxPlayers}` : '–';
      const extra = [info.map ? `Map: ${info.map}` : '', info.version ? `Version: ${info.version}` : '', info.motd ? `MOTD: ${info.motd}` : ''].filter(Boolean).join('\n');
      const msg = `${online ? 'Online' : 'Offline'}\nLatenz: ${lat}\nSpieler: ${players}${extra ? `\n${extra}` : ''}`;
      alert(msg);
    } catch (e) {
      alert('Statusprüfung fehlgeschlagen');
    }
  }

  async function removeItem(id) {
    if (!isAdmin) return; // defensive guard for guests
    if (!window.confirm('Diesen Server wirklich löschen?')) return;
    try { await ServersAPI.remove(id); load(); } catch { alert('Löschen fehlgeschlagen'); }
  }

  function startEdit(it) {
    if (!isAdmin) return; // defensive guard for guests
    setEditing(it);
    setEditForm({
      name: it.name || '',
      game: it.game || '',
      host: it.host || '',
      port: it.port || '',
      queryType: it.queryType || '',
      description: it.description || '',
      visibility: it.visibility || 'public',
      tags: Array.isArray(it.tags) ? it.tags.join(', ') : '',
      password: '',
    });
  }

  async function onCreate(e) {
    e.preventDefault();
    if (!canCreate) return;
    try {
      const payload = {
        name: form.name.trim(),
        game: form.game.trim() || 'generic',
        host: form.host.trim(),
        port: form.port ? parseInt(form.port, 10) : undefined,
        queryType: form.queryType.trim() || undefined,
        description: form.description || undefined,
        visibility: form.visibility || 'public',
        tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
        password: form.password ? form.password : undefined,
      };
      await ServersAPI.create(payload);
      setCreating(false);
      setForm({ name: '', game: '', host: '', port: '', queryType: '', description: '', visibility: 'public', tags: '', password: '' });
      load();
    } catch (e) {
      alert('Anlegen fehlgeschlagen');
    }
  }

  async function onEditSubmit(e) {
    e.preventDefault();
    if (!editing) return;
    try {
      const payload = {
        name: editForm.name.trim(),
        game: editForm.game.trim() || 'generic',
        host: editForm.host.trim(),
        port: editForm.port ? parseInt(editForm.port, 10) : undefined,
        queryType: editForm.queryType.trim() || undefined,
        description: editForm.description || undefined,
        visibility: editForm.visibility || 'public',
        tags: editForm.tags ? editForm.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      if (editForm.password) payload.password = editForm.password;
      await ServersAPI.update(editing.id, payload);
      setEditing(null);
      load();
    } catch (e) {
      alert('Speichern fehlgeschlagen');
    }
  }

  return (
    <div className="home-page">
      {/* Hero */}
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width:'100vw', margin:0, padding:0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Game-Server</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth:760 }}>Öffentliche und Mitglieder‑Server mit Live‑Status, Spielerslots und mehr.</p>
      </section>

      {/* Content */}
      <section style={{ width:'100%', maxWidth:1100, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:16 }}>
          {/* Left: list + filters */}
          <div>
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12, marginBottom:10 }}>
              <form onSubmit={onSearch} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Suche (Name/Host/Game)" style={{ minWidth: 240 }} />
                <input value={game} onChange={e=>setGame(e.target.value)} placeholder="Game (z.B. minecraft)" style={{ minWidth: 160 }} />
                <button type="submit">Suchen</button>
              </form>
            </div>

            <div style={{ marginBottom: 12, color: '#cbd5e1', fontSize: 13 }}>
              {loading ? 'Lade…' : `${total} Einträge`}
              {error && <span style={{ color: '#f87171', marginLeft: 12 }}>{error}</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 12 }}>
              {items.map(it => (
                <div key={it.id} ref={bindCardRef(it.id)} style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12, background: 'rgba(255,255,255,0.03)', position: 'relative' }}>
                  {/* Header with large game cover like Matchmaking */}
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                    <div title={it.game}>
                      <ServerGameIcon name={it.game} cache={rawgImages} setCache={setRawgImages} variant="cover" size={112} />
                    </div>
                    <div style={{ minWidth:0, flex:1 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                        <div style={{ fontWeight:600 }}>{it.name}</div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          {it.queryType && <span title={`Query: ${it.queryType}`} style={{ fontSize: 12, color: '#c084fc', background:'rgba(145,70,255,0.15)', border:'1px solid rgba(145,70,255,0.35)', padding:'2px 6px', borderRadius:6 }}>{it.queryType}</span>}
                          <span title={it.visibility} style={{ fontSize: 12, color: '#9ca3af' }}>{it.visibility}</span>
                        </div>
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 6 }}>{it.game || 'generic'}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 13, marginTop: 4 }}>{it.host}{it.port ? `:${it.port}` : ''}</div>
                    </div>
                  </div>
                  {/* Live status inline */}
                  <ServerLiveBadge live={statuses[it.id]} />
                  <ServerLiveDetails live={statuses[it.id]} queryType={it.queryType} />
                  {/* Password visible to everyone as requested */}
                  {it.password && (
                    <div style={{ fontFamily: 'monospace', fontSize: 13, marginTop: 4 }}>Passwort: {it.password}</div>
                  )}
                  {it.description && <div style={{ marginTop: 6, fontSize: 13 }}>{it.description}</div>}
                  {Array.isArray(it.tags) && it.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {it.tags.map((t, idx) => (
                        <span key={idx} style={{ fontSize: 12, background: 'rgba(145,70,255,0.15)', color: '#c084fc', border: '1px solid rgba(145,70,255,0.35)', padding: '2px 6px', borderRadius: 6 }}>{t}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button type="button" onClick={() => checkStatus(it.id)}>Status</button>
                    {isAdmin && (
                      <>
                        <button type="button" onClick={() => startEdit(it)}>Bearbeiten</button>
                        <button type="button" style={{ background: '#ef4444', color: 'white' }} onClick={() => removeItem(it.id)}>Löschen</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <button disabled={page<=1} onClick={()=>setPage(p => Math.max(1, p-1))}>Zurück</button>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>Seite {page}</div>
              <button disabled={(page*pageSize)>=total} onClick={()=>setPage(p => p+1)}>Weiter</button>
              <select value={pageSize} onChange={e=>setPageSize(parseInt(e.target.value,10))}>
                {[10,20,30,50].map(n => <option key={n} value={n}>{n}/Seite</option>)}
              </select>
            </div>
          </div>

          {/* Right: admin create/edit */}
          <div>
            {isAdmin && (
              <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
                <h3 style={{ margin:'8px 0' }}>Server verwalten</h3>
                {!creating && !editing && (
                  <button onClick={() => setCreating(true)}>Neuen Server anlegen</button>
                )}
                {creating && (
                  <>
                    <form onSubmit={onCreate} style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginTop: 8 }}>
                      <input required value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Name (wird in der Liste angezeigt)" />
                      <input value={form.game} onChange={e=>setForm(f=>({...f,game:e.target.value}))} placeholder="Game (z.B. minecraft, csgo, rust, ark, fivem)" />
                      <input required value={form.host} onChange={e=>setForm(f=>({...f,host:e.target.value}))} placeholder="Host (Domain oder IP, ohne http://)" />
                      <input value={form.port} onChange={e=>setForm(f=>({...f,port:e.target.value}))} placeholder="Port (nur Zahl, abhängig vom Spieltyp)" />
                      <input value={form.queryType} onChange={e=>setForm(f=>({...f,queryType:e.target.value}))} placeholder="Query-Type (generic, source/csgo, rust, ark, minecraft/mc, fivem/cfx)" />
                      <select value={form.visibility} onChange={e=>setForm(f=>({...f,visibility:e.target.value}))}>
                        <option value="public">public</option>
                        <option value="members">members</option>
                        <option value="private">private</option>
                      </select>
                      <input value={form.tags} onChange={e=>setForm(f=>({...f,tags:e.target.value}))} placeholder="Tags (kommagetrennt, z.B. EU, PvP)" />
                      <input type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="Passwort (optional – wird öffentlich angezeigt)" />
                      <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Beschreibung (optional)" style={{ minHeight: 100 }} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="submit">Anlegen</button>
                        <button type="button" onClick={()=>{ setCreating(false); }}>Abbrechen</button>
                      </div>
                    </form>
                    {/* Admin‑Hinweise nur während des Anlegens sichtbar */}
                    <div style={{ marginTop: 12, padding: 12, border:'1px solid rgba(255,255,255,0.12)', borderRadius: 8, background:'rgba(0,0,0,0.25)', color:'#cbd5e1' }}>
                      <div style={{ fontWeight:700, color:'#e5e7eb', marginBottom: 8 }}>Unterstützte Spieltypen, Felder und Eingabe‑Hinweise</div>
                      <ul style={{ margin:0, paddingLeft: 18, lineHeight: 1.6 }}>
                        <li>
                          <b>Host</b>: Domain oder IP ohne Protokoll (z. B. <code>example.org</code> oder <code>1.2.3.4</code>), keine Pfade.
                        </li>
                        <li>
                          <b>Port</b>: Nur die Portnummer (ohne Doppelpunkt). Falls leer, wird ein Standard nach Spieltyp genutzt (s. unten).
                        </li>
                        <li>
                          <b>Game</b>: Freitext für Anzeige und Icon‑Erkennung. Für beste Icons kurze, gängige Namen (z. B. <code>minecraft</code>, <code>csgo</code>, <code>rust</code>).
                          Es wird zuerst ein lokales Icon gesucht, dann ein Cover aus SteamGridDB → IGDB → (RAWG/Steam) geladen, sonst ein Badge angezeigt.
                        </li>
                        <li>
                          <b>Query‑Type</b>: Steuert, wie Live‑Daten abgefragt werden. Erlaubte Werte und Standard‑Ports:
                          <ul>
                            <li><b>generic</b>: TCP‑Probe; Port beliebig/optional. Zeigt Online/Offline + Latenz.</li>
                            <li><b>source</b> / <b>csgo</b>: A2S_INFO (UDP). Standard‑Query‑Port <code>27015</code>. Zeigt Spieler/Max, Map, Spielname, Servername, Latenz.</li>
                            <li><b>rust</b>: A2S_INFO (UDP). Häufig Query‑Port = GamePort+1 (z. B. Game 28015 → Query <code>28016</code>). Zeigt Spieler/Max, Map, Servername, Latenz.</li>
                            <li><b>ark</b>: A2S_INFO (UDP). Standard‑Query‑Port <code>27015</code>. Zeigt Spieler/Max, Map, Servername, Latenz.</li>
                            <li><b>minecraft</b> / <b>mc</b>: Status‑Ping (TCP). Standard‑Port <code>25565</code>. Zeigt Spieler/Max, Version, MOTD, Latenz.</li>
                            <li><b>fivem</b> / <b>cfx</b>: HTTP‑API. Standard‑Port <code>30120</code>. Liefert Hostname, Gamename, Spieler/Max, Ressourcenanzahl, Latenz.</li>
                          </ul>
                        </li>
                        <li>
                          <b>Passwort</b>: Optional; wird auf der öffentlichen Liste angezeigt. Nur setzen, wenn es für Clients benötigt wird.
                        </li>
                        <li>
                          <b>Tags</b>: Kommagetrennt für Filter (z. B. <code>EU, PvP, Modded</code>).
                        </li>
                      </ul>
                      <div style={{ marginTop: 8, fontSize: 12, color:'#9ca3af' }}>
                        Beispiele:
                        <ul style={{ margin:0, paddingLeft: 18 }}>
                          <li><code>queryType=csgo</code>, Host=<code>cs.example.org</code>, Port=<code>27015</code></li>
                          <li><code>queryType=rust</code>, Host=<code>rust.example.org</code>, Port=<code>28016</code> (wenn GamePort 28015)</li>
                          <li><code>queryType=minecraft</code>, Host=<code>mc.example.org</code>, Port leer (nutzt 25565)</li>
                          <li><code>queryType=fivem</code>, Host=<code>fivem.example.org</code>, Port=<code>30120</code></li>
                        </ul>
                      </div>
                    </div>
                  </>
                )}
                {editing && (
                  <div style={{ marginTop: 12 }}>
                    <h4 style={{ margin:'8px 0' }}>Server bearbeiten</h4>
                    <form onSubmit={onEditSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                      <input required value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} placeholder="Name" />
                      <input value={editForm.game} onChange={e=>setEditForm(f=>({...f,game:e.target.value}))} placeholder="Game" />
                      <input required value={editForm.host} onChange={e=>setEditForm(f=>({...f,host:e.target.value}))} placeholder="Host" />
                      <input value={editForm.port} onChange={e=>setEditForm(f=>({...f,port:e.target.value}))} placeholder="Port" />
                      <input value={editForm.queryType} onChange={e=>setEditForm(f=>({...f,queryType:e.target.value}))} placeholder="Query-Type" />
                      <select value={editForm.visibility} onChange={e=>setEditForm(f=>({...f,visibility:e.target.value}))}>
                        <option value="public">public</option>
                        <option value="members">members</option>
                        <option value="private">private</option>
                      </select>
                      <input value={editForm.tags} onChange={e=>setEditForm(f=>({...f,tags:e.target.value}))} placeholder="Tags (kommagetrennt)" />
                      <input type="password" value={editForm.password} onChange={e=>setEditForm(f=>({...f,password:e.target.value}))} placeholder="Neues Passwort (leer lassen = unverändert)" />
                      <textarea value={editForm.description} onChange={e=>setEditForm(f=>({...f,description:e.target.value}))} placeholder="Beschreibung" style={{ minHeight: 100 }} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="submit">Speichern</button>
                        <button type="button" onClick={()=>setEditing(null)}>Abbrechen</button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ServerGameIcon({ name, cache, setCache, variant = 'square', size = 36 }) {
  const [localFailed, setLocalFailed] = useState(false);
  const [rawgUrl, setRawgUrl] = useState(cache[name] ?? null);
  const placeholderH = Math.round(size * 0.5625); // ~16:9

  useEffect(() => {
    let mounted = true;
    if (!name) return;
    (async () => {
      if (!localFailed) return; // Only fetch when local icon failed
      if (cache[name] !== undefined) { setRawgUrl(cache[name]); return; }
      const url = await fetchRawgImageUrl(name);
      if (!mounted) return;
      setCache(prev => ({ ...prev, [name]: url }));
      setRawgUrl(url);
    })();
    return () => { mounted = false; };
  }, [name, localFailed]);

  const badge = variant === 'cover'
    ? (
      <div style={{ width: size, height: placeholderH, borderRadius:8, background: gameColor(name).bg, color: gameColor(name).fg, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize: Math.max(12, Math.round(size*0.16)) }}>
        {gameInitials(name)}
      </div>
    ) : (
      <div style={{ position:'absolute', inset:0, borderRadius:8, background: gameColor(name).bg, color: gameColor(name).fg, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:12 }}>
        {gameInitials(name)}
      </div>
    );

  const localPath = getGameIconPath(name);
  if (variant === 'cover') {
    return (
      <div style={{ width: size, display:'inline-block', lineHeight: 0 }}>
        {!localFailed && (
          <img src={localPath} alt={name} style={{ width: size, height: 'auto', maxHeight: 'none', borderRadius:8, display:'block', boxShadow:'0 1px 2px rgba(0,0,0,0.35)' }} onError={() => setLocalFailed(true)} />
        )}
        {localFailed && rawgUrl && (
          <img src={rawgUrl} alt={name} style={{ width: size, height: 'auto', maxHeight: 'none', borderRadius:8, display:'block', boxShadow:'0 1px 2px rgba(0,0,0,0.35)' }} onError={() => setRawgUrl(null)} />
        )}
        {localFailed && !rawgUrl && badge}
      </div>
    );
  }
  // square (icon) variant
  return (
    <div style={{ position:'relative', width: size, height: size }}>
      <img src={localPath} alt={name} style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'contain', filter:'drop-shadow(0 1px 1px rgba(0,0,0,0.4))', display: localFailed ? 'none' : 'block' }} onError={() => setLocalFailed(true)} />
      {localFailed && rawgUrl && (
        <img src={rawgUrl} alt={name} style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }} onError={() => setRawgUrl(null)} />
      )}
      {localFailed && !rawgUrl && badge}
    </div>
  );
}

function ServerLiveBadge({ live }) {
  const online = live?.online === true;
  const lat = typeof live?.latencyMs === 'number' ? `${live.latencyMs}ms` : '–';
  const players = (typeof live?.players === 'number' && typeof live?.maxPlayers === 'number') ? `${live.players}/${live.maxPlayers}` : (typeof live?.players === 'number' ? `${live.players}` : '–');
  const extras = [live?.map ? `Map: ${live.map}` : '', live?.version ? `Version: ${live.version}` : ''].filter(Boolean).join(' · ');
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, fontSize: 12 }}>
      <span style={{ width:8, height:8, borderRadius:'50%', background: online ? '#22c55e' : '#ef4444', display:'inline-block' }} title={online ? 'Online' : 'Offline'} />
      <span style={{ color:'#9ca3af' }}>{online ? 'Online' : 'Offline'}</span>
      <span style={{ color:'#9ca3af' }}>Latenz: {lat}</span>
      <span style={{ color:'#9ca3af' }}>Spieler: {players}</span>
      {extras && <span style={{ color:'#9ca3af' }}>{extras}</span>}
    </div>
  );
}

function ServerLiveDetails({ live, queryType }) {
  if (!live || live.online !== true) return null;
  const t = String(queryType || '').toLowerCase();
  const items = [];
  // Common
  if (typeof live.latencyMs === 'number') items.push({ k: 'Latenz', v: `${live.latencyMs}ms` });
  if (typeof live.players === 'number' && typeof live.maxPlayers === 'number') items.push({ k: 'Spieler', v: `${live.players}/${live.maxPlayers}` });
  else if (typeof live.players === 'number') items.push({ k: 'Spieler', v: `${live.players}` });
  // Per type extras
  if (t === 'source' || t === 'csgo' || t === 'rust' || t === 'ark') {
    if (live.map) items.push({ k: 'Map', v: live.map });
    if (live.name) items.push({ k: 'Server', v: live.name });
    if (live.game) items.push({ k: 'Spiel', v: live.game });
  }
  if (t === 'minecraft' || t === 'mc') {
    if (live.version) items.push({ k: 'Version', v: live.version });
    if (live.motd) items.push({ k: 'MOTD', v: live.motd });
  }
  if (t === 'fivem' || t === 'cfx') {
    if (live.hostname) items.push({ k: 'Hostname', v: live.hostname });
    if (live.gamename) items.push({ k: 'Game', v: live.gamename });
    if (Array.isArray(live.resources)) items.push({ k: 'Ressourcen', v: String(live.resources.length) });
  }
  if (items.length === 0) return null;
  return (
    <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', columnGap: 12, rowGap: 6, marginTop: 8, fontSize: 12, color:'#cbd5e1' }}>
      {items.map((it, idx) => (
        <div key={idx} style={{ display:'contents' }}>
          <div style={{ color:'#9ca3af' }}>{it.k}</div>
          <div>{it.v}</div>
        </div>
      ))}
    </div>
  );
}
